import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { getSupabase } from '../lib/supabase/client';
import type {
  SupabaseProjectInsert,
  SupabaseProjectRow,
} from '../types/supabase';

// ---------------------------------------------------------------------------
// Domain shape — wider than the legacy `Project` in src/types/index.ts.
// ---------------------------------------------------------------------------
//
// The legacy `Project` type (used across existing UI) has shape:
//   { id, name, scenes, createdAt, updatedAt }
//
// Supabase rows additionally carry `description` and `owner_id`. We surface
// those at the hook boundary so callers that care about them (e.g. project
// settings) get them, while preserving the legacy field set so existing
// components keep slotting in unchanged.
//
// `scenes` defaults to `[]` here — Supabase scenes live in a separate table
// and are loaded by `useSupabaseScenes` (sibling hook in Sprint B). Callers
// that need scenes must compose the two hooks.
export interface SupabaseProject {
  id: string;
  name: string;
  description: string | null;
  ownerId: string;
  createdAt: string;
  updatedAt: string;
  scenes: never[]; // typed empty; sibling hook hydrates scenes separately.
}

export interface CreateProjectInput {
  name: string;
  description?: string;
}

// ---------------------------------------------------------------------------
// Query keys (exported for invalidation from sibling hooks/tests)
// ---------------------------------------------------------------------------

export const supabaseProjectsKey = ['supabase', 'projects'] as const;

// ---------------------------------------------------------------------------
// Mapper: Postgres row (snake_case) → frontend Project (camelCase)
// ---------------------------------------------------------------------------

function mapRow(row: SupabaseProjectRow): SupabaseProject {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    ownerId: row.owner_id,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    scenes: [],
  };
}

// ---------------------------------------------------------------------------
// useSupabaseProjects — list all projects scoped by RLS to the current owner
// ---------------------------------------------------------------------------

export function useSupabaseProjects(): UseQueryResult<SupabaseProject[], Error> {
  return useQuery<SupabaseProject[], Error>({
    queryKey: supabaseProjectsKey,
    queryFn: async () => {
      const { data, error } = await getSupabase()
        .from('projects')
        .select('id, name, description, owner_id, created_at, updated_at')
        .order('updated_at', { ascending: false });

      if (error) throw new Error(error.message);
      const rows = (data ?? []) as SupabaseProjectRow[];
      return rows.map(mapRow);
    },
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// useCreateSupabaseProject — insert + invalidate list
// ---------------------------------------------------------------------------
//
// owner_id is `NOT NULL REFERENCES auth.users(id)` in the migration with
// no default, so we MUST set it explicitly from the current session. RLS
// also enforces `owner_id = auth.uid()` on insert, so this acts as both a
// schema requirement and an auth assertion.

export function useCreateSupabaseProject(): UseMutationResult<
  SupabaseProject,
  Error,
  CreateProjectInput
> {
  const qc = useQueryClient();

  return useMutation<SupabaseProject, Error, CreateProjectInput>({
    mutationFn: async (input) => {
      const { data: sessionData } = await getSupabase().auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) {
        throw new Error('not authenticated');
      }

      const payload: SupabaseProjectInsert = {
        name: input.name,
        description: input.description ?? null,
        owner_id: userId,
      };

      const { data, error } = await getSupabase()
        .from('projects')
        .insert(payload)
        .select('id, name, description, owner_id, created_at, updated_at')
        .single();

      if (error) throw new Error(error.message);
      if (!data) throw new Error('insert returned no row');
      return mapRow(data as SupabaseProjectRow);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: supabaseProjectsKey });
    },
  });
}

// ---------------------------------------------------------------------------
// useDeleteSupabaseProject — RLS prevents deleting others' projects
// ---------------------------------------------------------------------------

export function useDeleteSupabaseProject(): UseMutationResult<
  void,
  Error,
  string
> {
  const qc = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (projectId) => {
      const { error } = await getSupabase()
        .from('projects')
        .delete()
        .eq('id', projectId);

      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: supabaseProjectsKey });
    },
  });
}
